import store from "@/store";
import { mapGetters } from "vuex";
import { PUBLIC_HEADER } from "@/assets/js/config";

function isVisibleNode(node) {
  return !!node && !node.hidden && node.route !== "0";
}

function getNodeTitle(node) {
  return (node.meta && node.meta.title) || node.title || node.name || "";
}

const menuTranslations = {
  首页: 'home', 登录: 'login', 版面检索: 'pageSearch', 全库检索: 'fullLibrary',
  文章检索: 'articleSearch', 图片检索: 'photoSearch', 广告检索: 'adSearch',
  检索统计: 'censusSearch', 专题列表: 'featureList', 图片处理: 'imageProcess',
  报史资料: 'source', 细览页面: 'detail', 图片细览页: 'imageDetail',
  期刊详情: 'journalDetail', 期刊浏览: 'journalBrowse', 修改密码: 'changePassword'
};

function translateMenuName(name, vm) {
  const key = menuTranslations[name];
  return key ? vm.$t(`common.${key}`) : name;
}

function isHomeMenuItem(item) {
  if (!item) return false;
  const title = (item.meta && item.meta.title) || item.title || item.name || "";
  const path = item.path || "";
  if (title === "首页") return true;
  return path === "/" || path === "/home" || path === "/dashboard/analysis";
}

function collectLeafMenus(nodes = [], bucket = []) {
  nodes.forEach((node) => {
    if (!isVisibleNode(node)) return;
    const hasChildren = Array.isArray(node.children) && node.children.length;
    const isLayout = (node.component || "").includes("layouts/");
    if (hasChildren && isLayout) {
      collectLeafMenus(node.children, bucket);
      return;
    }
    const title = getNodeTitle(node);
    if (!title || !node.path) return;
    bucket.push({ path: node.path, name: title, meta: node.meta ? { ...node.meta } : {} });
  });
  return bucket;
}

function buildHeaderMenuGroups(menuTree = []) {
  const groups = [];
  (menuTree || []).forEach((node) => {
    if (!isVisibleNode(node)) return;
    const title = getNodeTitle(node);
    if (!title || !node.path) return;
    const children = Array.isArray(node.children) && node.children.length ? collectLeafMenus(node.children, []) : [];
    groups.push({
      path: node.path,
      name: title,
      meta: node.meta ? { ...node.meta } : {},
      children,
    });
  });
  return groups;
}

export const menuMix = {
  data() {
    return {
      currentPage: "",
      drawer: false,
    };
  },
  computed: {
    ...mapGetters("permission", ["topMenus", "menuTree"]),
    topNavList() {
      if (this.headerMenuGroups && this.headerMenuGroups.length) {
        return this.headerMenuGroups.map((g) => ({
          path: g.path,
          name: translateMenuName(g.name, this),
          meta: g.meta || {},
        }));
      }
      return this.publicList || [];
    },
    publicList() {
      if (this.topMenus && this.topMenus.length) {
        return this.topMenus.map((item) => ({
          ...item,
          name: translateMenuName(item.name || item.title || (item.meta && item.meta.title), this)
        }));
      }
      return PUBLIC_HEADER.map((item) => ({
        ...item,
        name: translateMenuName(item.name, this)
      }));
    },
    /**
     * 顶部一级菜单（用于横向 el-menu + el-submenu）
     * - 保留后端一级结构
     * - children 为可跳转的叶子菜单
     */
    headerMenuGroups() {
      if (this.menuTree && this.menuTree.length) {
        return buildHeaderMenuGroups(this.menuTree).map((group) => ({
          ...group,
          name: translateMenuName(group.name, this),
          children: (group.children || []).map((item) => ({
            ...item,
            name: translateMenuName(item.name, this)
          }))
        }));
      }
      // 无动态菜单时，退回旧的 publicList 扁平结构
      return (this.publicList || []).map((m) => ({ ...m, children: [] }));
    },
    /**
     * 是否使用“横向一级 + 下拉二级”的顶部菜单样式
     * 规则：当一级菜单（包含首页）多于2项时启用
     */
    useHorizontalMenu() {
      const groups = this.headerMenuGroups || [];
      return groups.length > 2;
    },
    activeTopPath() {
      const groups = this.headerMenuGroups || [];
      if (!groups.length) {
        return this.currentPage;
      }
      const current = this.currentPage || "";
      const matched = groups.find((g) => {
        if (g.path === current) return true;
        return (g.children || []).some(
          (c) => c.path === current || (c.path && current.indexOf(c.path) === 0)
        );
      });
      return matched ? matched.path : groups[0].path;
    },
    secondNavList() {
      const groups = this.headerMenuGroups || [];
      if (!groups.length) return [];
      const currentGroup = groups.find((g) => g.path === this.activeTopPath);
      return (currentGroup && currentGroup.children) || [];
    },
    isCompactHeaderMode() {
      const topList = this.topNavList || [];
      const businessTopList = topList.filter((i) => i && i.path && !isHomeMenuItem(i));
      if (businessTopList.length !== 1) return false;
      const businessTop = businessTopList[0];
      const groups = this.headerMenuGroups || [];
      const businessGroup =
        groups.find((g) => g.path === businessTop.path) ||
        groups.find((g) => !isHomeMenuItem(g));
      return !!businessGroup && Array.isArray(businessGroup.children) && businessGroup.children.length > 0;
    },
    compactHeaderNavList() {
      if (!this.isCompactHeaderMode) return [];
      const topList = this.topNavList || [];
      const home = topList.find((i) => isHomeMenuItem(i));
      const businessTop = topList.find((i) => i && i.path && !isHomeMenuItem(i));
      const groups = this.headerMenuGroups || [];
      const businessGroup =
        (businessTop && groups.find((g) => g.path === businessTop.path)) ||
        groups.find((g) => !isHomeMenuItem(g));
      const children = (businessGroup && businessGroup.children) || [];
      return home ? [home, ...children] : children;
    },
  },
  watch: {
    publicList: {
      handler() {
        this.syncCurrentFromRoute();
      },
      immediate: true,
    },
    $route() {
      this.syncCurrentFromRoute();
    },
  },
  methods: {
    jumpPage(path) {
      this.drawer = false;
      this.$router.push(path);
      this.currentPage = path;
    },
    syncCurrentFromRoute() {
      const topList = this.topNavList || [];
      const secondList = this.headerMenuGroups
        ? this.headerMenuGroups.reduce((arr, g) => arr.concat(g.children || []), [])
        : [];
      const allMenus = [...topList, ...secondList];
      if (!allMenus.length) {
        this.currentPage = "";
        return;
      }
      let targetPath = this.$route.path;
      switch (targetPath) {
        case "/detail":
        case "/journal/journalDetail":
        case "/yearbook/yearbookDetail":
          try {
            targetPath =
              window.sessionStorage.getItem("hr_lastPath") ||
              this.$route.query.lastPath ||
              "";
          } catch (e) {
            targetPath = this.$route.query.lastPath || "";
          }
          break;
        case "/imageDetail":
          targetPath = "/photoSearch";
          break;
        case "/yearbook/yearbookOverview":
          targetPath = "/yearbook/yearbookBrowse";
          break;
        case "/home":
          targetPath = "/";
          break;
        default:
          break;
      }
      let exist = allMenus.find((item) => item.path === targetPath);
      if (!exist) {
        exist = allMenus.find(
          (item) => item.path !== "/" && targetPath.indexOf(item.path) === 0
        );
      }
      this.currentPage = exist ? exist.path : '/yearbook/yearbookBrowse';
    },
    handleCommand(flag) {
      switch (flag) {
        case "exit":
          store.dispatch("user/exitLogin");
          break;
        case "password":
          let routeUrl = this.$router.resolve({
            path: "/personalCenterPassword",
            query: {},
          });
          window.open(routeUrl.href, "_blank");
          break;
        case "userMessage":
          this.drawer = false;
          this.$root.$emit("open-user-message-dialog");
          break;
      }
    },
  },
};
